import { buildApiUrl } from "@/lib/appPaths";

export interface InviteCodeSummary {
  has_code: boolean;
  code: string | null;
  status: string | null;
  reward_tcoins: number;
  reward_generation_quota: number;
  reward_generation_spec_code: string;
  share_url: string | null;
  successful_invite_count: number;
  earned_tcoins: number;
  earned_generation_quota: number;
  program_enabled: boolean;
  display_title: string | null;
  display_description: string | null;
}

export interface InviteCodeCreateResult {
  code: string;
  share_url: string;
  reward_tcoins: number;
  reward_generation_quota: number;
  reward_generation_spec_code: string;
}

export interface InviteCodeValidation {
  valid: boolean;
  code: string | null;
  reward_tcoins: number;
  reward_generation_quota: number;
  reward_generation_spec_code: string;
  message: string;
}

type BaseResponse<T> = {
  success: boolean;
  code: number;
  message: string;
  data: T;
};

type ErrorPayload = {
  detail?: string | { msg?: string; code?: string };
  message?: string;
};

function resolveErrorMessage(payload: ErrorPayload | null, fallback: string) {
  if (typeof payload?.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }
  if (typeof payload?.detail === "object" && payload.detail?.msg) {
    return payload.detail.msg;
  }
  return payload?.message || fallback;
}

async function requestJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    ...init,
    credentials: "include",
  });
  const payload = (await response.json().catch(() => null)) as BaseResponse<T> | ErrorPayload | null;
  const success = Boolean(payload && "success" in payload && payload.success);
  if (!response.ok || !success) {
    throw new Error(resolveErrorMessage(payload as ErrorPayload | null, "Invitation API request failed"));
  }
  return (payload as BaseResponse<T>).data;
}

export function fetchMyInviteCode() {
  return requestJson<InviteCodeSummary>(buildApiUrl("/invitations/me"));
}

export function createMyInviteCode() {
  return requestJson<InviteCodeCreateResult>(buildApiUrl("/invitations/me/code"), {
    method: "POST",
  });
}

export function validateInviteCode(code: string) {
  return requestJson<InviteCodeValidation>(buildApiUrl(`/invitations/validate?code=${encodeURIComponent(code)}`));
}
